import { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';

const SIM_DEFS = '/machinesim/api/sim-defs';

function apiErr(e) {
  const fromBody =
    e.response?.data &&
    typeof e.response.data === 'object' &&
    e.response.data.error != null
      ? String(e.response.data.error)
      : null;
  return fromBody || e.message || 'Request failed';
}

export default function StreamMachineTypeCoreFields() {
  const { machineTypeId } = useParams();
  const [mt, setMt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [patchError, setPatchError] = useState(null);
  const [patchingId, setPatchingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${SIM_DEFS}/machine-types/${machineTypeId}`);
      setMt(res.data);
    } catch (e) {
      setError(apiErr(e));
      setMt(null);
    } finally {
      setLoading(false);
    }
  }, [machineTypeId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleCore = async (fieldId, next) => {
    setPatchError(null);
    setPatchingId(fieldId);
    try {
      await axios.patch(`${SIM_DEFS}/fields/${fieldId}`, { isCore: next });
      setMt((prev) => {
        if (!prev?.streamFields) return prev;
        return {
          ...prev,
          streamFields: prev.streamFields.map((f) =>
            f.id === fieldId ? { ...f, isCore: next } : f,
          ),
        };
      });
    } catch (e) {
      setPatchError(apiErr(e));
    } finally {
      setPatchingId(null);
    }
  };

  if (loading) {
    return <div className="p-8 text-muted-foreground">Loading…</div>;
  }

  if (error || !mt) {
    return (
      <div className="p-4 md:p-8 max-w-xl mx-auto space-y-4">
        <p className="text-destructive">{error || 'Not found'}</p>
        <Link to="/stream/machine-types" className="text-primary text-sm hover:underline">
          ← Machine type definitions
        </Link>
      </div>
    );
  }

  const fields = [...(mt.streamFields || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto pb-16">
      <div className="mb-6">
        <Link to="/stream/machine-types" className="text-sm text-muted-foreground hover:text-foreground">
          ← Machine type definitions
        </Link>
        <h2 className="text-xl font-semibold text-foreground mt-2">Core fields</h2>
        <p className="text-sm text-muted-foreground mt-1 font-mono">
          {mt.code} — {mt.name}
        </p>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Fields marked <strong className="text-foreground">Core</strong> are the minimal set suggested for fleet-wide
          cards or summaries. This is stored per stream field (<span className="font-mono">isCore</span>). To apply the same
          rules to <strong className="text-foreground">all</strong> machine types at once, use{' '}
          <Link to="/stream/machine-types/global-core-keys" className="text-primary hover:underline">
            Global core keys
          </Link>
          .
        </p>
      </div>

      {patchError && (
        <p className="mb-4 text-sm text-cn-warning" role="alert">
          {patchError}
        </p>
      )}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-secondary text-left text-muted-foreground">
              <th className="p-3 font-medium w-12">Core</th>
              <th className="p-3 font-medium">fieldKey</th>
              <th className="p-3 font-medium">label</th>
              <th className="p-3 font-medium">dataType</th>
            </tr>
          </thead>
          <tbody className="font-mono text-foreground">
            {fields.map((f) => (
              <tr key={f.id} className="border-t border-border">
                <td className="p-3 align-middle">
                  <input
                    type="checkbox"
                    className="rounded border-border"
                    checked={f.isCore === true}
                    disabled={patchingId === f.id}
                    onChange={(e) => toggleCore(f.id, e.target.checked)}
                    aria-label={`Core: ${f.fieldKey}`}
                  />
                </td>
                <td className="p-3 text-primary">{f.fieldKey}</td>
                <td className="p-3 text-muted-foreground font-sans">{f.label}</td>
                <td className="p-3">{f.dataType}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {fields.length === 0 && (
        <p className="text-muted-foreground text-sm mt-4">No stream fields for this machine type.</p>
      )}
    </div>
  );
}
